/**
 * Point d'entrée du front.
 *
 * Coquille à navigation verticale : une barre latérale, une page à la fois.
 * Le tableau de bord est la page d'accueil, pas un onglet parmi d'autres.
 *
 * Aucune valeur n'est fabriquée ici : tout vient de l'API.
 */

import { anciennete, dateFr, entier, horodatageFr, type ParametresSimulateur } from '@scc/core';
import {
  ErreurApi,
  chargerSimulation,
  deconnecter,
  modules as chargerModules,
  session,
  tableauDeBord,
  type Charge,
  type Identite,
  type ModulesServis,
} from './api.js';
import { h, s as svg, vider } from './dom.js';
import { resumeClassement } from './modules/classement/public.js';
import { basculerTheme, initialiserTheme, themeActif } from './theme.js';
import { fabriqueDe, type ContexteVue } from './vues/registre.js';

const conteneur = document.getElementById('application');
if (!conteneur) throw new Error('conteneur applicatif absent');
/** Racine applicative, garantie non nulle par le garde ci-dessus. */
const racine: HTMLElement = conteneur;

interface Page {
  readonly cle: string;
  readonly libelle: string;
  readonly picto: string;
  readonly groupe: string;
  readonly construire: () => HTMLElement;
}

/**
 * Bandeau de fraîcheur.
 *
 * Les sources ne sont pas interrogées à chaque consultation : une campagne
 * quotidienne suffit, et ne dépend pas de la disponibilité de Zoho à l'instant
 * du clic. Le bandeau ne dit donc plus « direct ou dégradé » mais **« à jour ou
 * périmé »** — et signale à part le cas où aucune ingestion des sources n'a
 * jamais réussi, l'application servant alors l'instantané livré.
 */
function bandeauFraicheur(d: Charge): HTMLElement {
  const f = d.fraicheur;
  const maintenant = new Date().toISOString();
  const age = f.derniereIngestion ? anciennete(f.derniereIngestion, maintenant) : null;
  const cadence = `mise à jour toutes les ${entier(f.periodeHeures)} heures`;

  if (!f.derniereIngestion) {
    return h(
      'div',
      { classe: 'bandeau-fraicheur', role: 'status' },
      h('strong', {}, 'Aucune ingestion enregistrée — '),
      'l’application sert l’instantané livré avec l’image.',
    );
  }

  if (f.modeDegrade) {
    return h(
      'div',
      { classe: 'bandeau-fraicheur', role: 'status' },
      h('strong', {}, 'Instantané livré — '),
      `les sources n’ont pas encore été ingérées avec succès. Données du ${horodatageFr(f.derniereIngestion)}, ` +
        `soit il y a ${age}.`,
    );
  }

  if (f.aJour && f.partielle) {
    return h(
      'div',
      { classe: 'bandeau-fraicheur', role: 'status' },
      h('strong', {}, 'Ingestion partielle — '),
      `dernière campagne ${horodatageFr(f.derniereIngestion)} (il y a ${age}), ${cadence}. Une source n’a pas ` +
        `répondu : le détail figure sur l’écran Exploitation.`,
    );
  }

  if (f.aJour) {
    return h(
      'div',
      { classe: 'bandeau-fraicheur direct', role: 'status' },
      'Données à jour — dernière ingestion ',
      h('strong', {}, horodatageFr(f.derniereIngestion)),
      ` (il y a ${age}), ${cadence}.`,
    );
  }

  return h(
    'div',
    { classe: 'bandeau-fraicheur', role: 'status' },
    h('strong', {}, 'Données périmées — '),
    `dernière ingestion réussie le ${horodatageFr(f.derniereIngestion)}, soit il y a ${age}, alors que la ` +
      `cadence attendue est d’une campagne toutes les ${entier(f.periodeHeures)} heures.`,
  );
}

/**
 * Icône du bouble de thème : un croissant de lune en mode jour, un soleil en
 * mode nuit — on montre ce vers quoi le clic emmène, comme sur compagnies.org.
 */
function icoTheme(sombre: boolean): SVGElement {
  const commun = {
    width: 13,
    height: 13,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': 2,
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round',
    'aria-hidden': 'true',
  };
  if (sombre) {
    const soleil = svg('svg', commun);
    soleil.appendChild(svg('circle', { cx: 12, cy: 12, r: 5 }));
    for (const d2 of [
      'M12 1v2', 'M12 21v2', 'M4.22 4.22l1.42 1.42', 'M18.36 18.36l1.42 1.42',
      'M1 12h2', 'M21 12h2', 'M4.22 19.78l1.42-1.42', 'M18.36 5.64l1.42-1.42',
    ]) {
      soleil.appendChild(svg('path', { d: d2 }));
    }
    return soleil;
  }
  const lune = svg('svg', commun);
  lune.appendChild(svg('path', { d: 'M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z' }));
  return lune;
}

function monter(
  d: Charge,
  identite: Identite,
  nav: ModulesServis,
  parametresInitiaux?: Partial<ParametresSimulateur>,
  navigationSecours = false,
): void {
  let allerA: (cle: string) => void = () => undefined;
  /**
   * Recharge le tableau de bord et revient sur la page demandée.
   * `trajectoire` change l'hypothèse salariale servie par le serveur.
   */
  const recharger = (page?: string, trajectoire?: string): void => {
    void demarrer(page ?? cleCourante, trajectoire ?? trajectoireCourante);
  };

  const contexte: ContexteVue = {
    d,
    identite,
    parametresInitiaux,
    allerA: (cle) => {
      allerA(cle);
    },
    recharger,
  };

  // La navigation vient du serveur : modules actifs et entrées permises.
  const pages: Page[] = nav.navigation.flatMap((e) => {
    const fabrique = fabriqueDe(e.cle);
    if (!fabrique) return [];
    return [
      {
        cle: e.cle,
        libelle: e.libelle.replace('{exercice}', String(d.meta.exerciceCourant)),
        picto: e.picto,
        groupe: e.groupe,
        construire: () => fabrique(contexte),
      },
    ];
  });

  // Les vues sont construites une fois, puis masquées et démasquées : le
  // simulateur et la grille budgétaire conservent ainsi leur état.
  const noeuds = new Map<string, HTMLElement>();
  const boutons = new Map<string, HTMLButtonElement>();
  let cleCourante = pages[0]?.cle ?? 'tableau-bord';

  const zone = h('div', { classe: 'container', id: 'contenu' });

  allerA = (cle: string): void => {
    const page = pages.find((p) => p.cle === cle) ?? pages[0];
    if (!page) return;
    cleCourante = page.cle;

    let noeud = noeuds.get(page.cle);
    if (!noeud) {
      noeud = page.construire();
      noeuds.set(page.cle, noeud);
      zone.appendChild(noeud);
    }
    for (const [k, n] of noeuds) n.hidden = k !== page.cle;
    for (const [k, b] of boutons) {
      if (k === page.cle) b.setAttribute('aria-current', 'page');
      else b.removeAttribute('aria-current');
    }
    history.replaceState(null, '', `#${page.cle}${window.location.search}`);
    document.title = `${page.libelle} — Budget SCC`;
    window.scrollTo(0, 0);
  };

  // ── Barre latérale ────────────────────────────────────────────────────
  const nbAlertes = d.compteurAlertes.alerte;
  const menu = h('nav', { 'aria-label': 'Navigation principale' });
  const groupes = [...new Set(pages.map((p) => p.groupe))];
  for (const groupe of groupes) {
    const bloc = h('div', { classe: 'groupe-nav' });
    if (groupe !== '') bloc.appendChild(h('p', { classe: 'intitule' }, groupe));
    for (const p of pages.filter((x) => x.groupe === groupe)) {
      const b = h(
        'button',
        {
          classe: 'lien-nav',
          type: 'button',
          surClick: () => {
            allerA(p.cle);
          },
        },
        h('span', { classe: 'picto', 'aria-hidden': 'true' }, p.picto),
        h('span', {}, p.libelle),
        p.cle === 'alertes' && nbAlertes > 0
          ? h(
              'span',
              { classe: 'pastille', 'aria-label': `${nbAlertes} alertes` },
              entier(nbAlertes),
            )
          : null,
      );
      boutons.set(p.cle, b);
      bloc.appendChild(b);
    }
    menu.appendChild(bloc);
  }

  // Deux niveaux : l'`aside` s'étire sur toute la hauteur et porte le fond,
  // `.laterale-fixe` reste collé en haut de la fenêtre. Un même élément ne peut
  // pas faire les deux — voir la note dans `styles-gestion.css`.
  const laterale = h(
    'aside',
    { classe: 'laterale' },
    h(
      'div',
      { classe: 'laterale-fixe' },
      h(
        'div',
        { classe: 'laterale-marque' },
        h('p', { classe: 'titre' }, 'Budget SCC'),
        h('p', { classe: 'sous' }, `Exercice ${d.meta.exerciceCourant}`),
      ),
      menu,
      h(
        'div',
        { classe: 'laterale-pied' },
        h('p', { classe: 'qui' }, identite.nom ?? identite.courriel ?? ''),
        h('p', { classe: 'role' }, identite.role === 'administration' ? 'Direction' : 'Lecture'),
        h(
          'div',
          { classe: 'actions' },
          boutonTheme(),
          h(
            'button',
            {
              type: 'button',
              surClick: () => {
                window.print();
              },
            },
            'Imprimer',
          ),
          h(
            'button',
            {
              type: 'button',
              title: 'La session est commune : vous serez déconnecté·e de tous les sites du SCC.',
              surClick: () => {
                void (async () => {
                  const url = await deconnecter();
                  window.location.href = url ?? '/';
                })();
              },
            },
            'Se déconnecter',
          ),
        ),
      ),
    ),
  );

  // Le pied de page appartient à la colonne de contenu, pas au document : placé
  // après la coquille, il occupait le bas de la fenêtre et la colonne sombre
  // s'arrêtait au-dessus de lui. Dans la coquille, celle-ci descend jusqu'en bas.
  const pied = h(
    'footer',
    {},
    h(
      'div',
      { classe: 'container' },
      `Sources : comptabilité Zoho Books · base Directus des adhérents et de la paie · ` +
        `${d.meta.versionPrevisionnel} — données arrêtées au ${dateFr(d.meta.dateArrete)}`,
      h('br'),
      'Document destiné à usage interne FSICPA / SCC',
    ),
  );

  /**
   * Bouton de bascule. Le thème change les couleurs des graphiques, qui les
   * lisent au moment du rendu : on remonte donc l'interface entière plutôt que
   * de laisser des SVG peints avec la palette précédente.
   */
  function boutonTheme(): HTMLElement {
    const sombre = themeActif() === 'dark';
    return h(
      'button',
      {
        type: 'button',
        classe: 'bascule-theme',
        'aria-label': sombre ? 'Passer en mode jour' : 'Passer en mode nuit',
        title: sombre ? 'Passer en mode jour' : 'Passer en mode nuit',
        surClick: () => {
          basculerTheme();
          monter(d, identite, nav, parametresInitiaux, navigationSecours);
        },
      },
      icoTheme(sombre),
      h('span', {}, sombre ? 'Jour' : 'Nuit'),
    );
  }

  vider(racine);
  racine.removeAttribute('aria-busy');
  racine.appendChild(bandeauFraicheur(d));
  // Module Classement : signale les écritures qui attendent un arbitrage. Un
  // échec de la route ne gêne pas le tableau de bord.
  if (nav.modules.some((m) => m.id === 'classement') && pages.some((p) => p.cle === 'arbitrage')) {
    const emplacement = h('div', {});
    racine.appendChild(emplacement);
    void resumeClassement()
      .then((r) => {
        const n = r.aVerifier + r.nonClasses;
        if (n <= 0) return;
        emplacement.appendChild(
          h(
            'div',
            { classe: 'bandeau-fraicheur', role: 'status' },
            h('strong', {}, `${entier(n)} écriture${n > 1 ? 's' : ''} à arbitrer — `),
            h(
              'button',
              {
                classe: 'btn-action',
                type: 'button',
                surClick: () => {
                  allerA('arbitrage');
                },
              },
              'ouvrir le classement',
            ),
          ),
        );
      })
      .catch(() => undefined);
  }
  if (navigationSecours) {
    racine.appendChild(
      h(
        'div',
        { classe: 'bandeau-fraicheur', role: 'status' },
        h('strong', {}, 'Navigation indisponible — '),
        'la liste des modules n’a pas pu être chargée : seul le tableau de bord est affiché. ' +
          'Rechargez la page pour retrouver toute la navigation.',
      ),
    );
  }
  racine.appendChild(
    h('div', { classe: 'coquille' }, laterale, h('main', { classe: 'zone' }, zone, pied)),
  );

  const ancre = window.location.hash.replace('#', '');
  allerA(pages.some((p) => p.cle === ancre) ? ancre : cleCourante);
}

/**
 * Page « accès réservé » : la personne est connectée au SCC mais n'a pas droit
 * au budget. Aucun lien de connexion — la page centrale la renverrait ici.
 */
function accesReserve(message: string, accueil: string | null): void {
  vider(racine);
  racine.removeAttribute('aria-busy');
  racine.appendChild(
    h(
      'div',
      { classe: 'container' },
      h(
        'div',
        { classe: 'erreur-bloc' },
        h('h1', {}, 'Accès réservé'),
        h('p', {}, message),
        h(
          'p',
          {},
          accueil ? h('a', { classe: 'btn-action', href: accueil }, 'Retour à compagnies.org') : null,
          ' ',
          h(
            'button',
            {
              type: 'button',
              title: 'La session est commune : vous serez déconnecté·e de tous les sites du SCC.',
              surClick: () => {
                void (async () => {
                  const url = await deconnecter();
                  window.location.href = url ?? '/';
                })();
              },
            },
            'Changer de compte',
          ),
        ),
      ),
    ),
  );
}

function erreur(message: string, connexion?: string): void {
  vider(racine);
  racine.removeAttribute('aria-busy');
  racine.appendChild(
    h(
      'div',
      { classe: 'container' },
      h(
        'div',
        { classe: 'erreur-bloc' },
        h('p', {}, message),
        connexion
          ? h('p', {}, h('a', { classe: 'btn-action', href: connexion }, 'Se connecter'))
          : null,
      ),
    ),
  );
}

/**
 * Navigation servie quand `/api/modules` ne répond pas : le tableau de bord
 * seul, pour ne pas rendre toute l'application inutilisable.
 */
const NAVIGATION_DE_SECOURS: ModulesServis = {
  modules: [{ id: 'noyau', nom: 'Noyau' }],
  navigation: [
    {
      cle: 'tableau-bord',
      libelle: 'Tableau de bord',
      picto: '◉',
      groupe: '',
      ordre: 0,
      droit: 'noyau.consulter',
      module: 'noyau',
    },
  ],
  reglages: {},
};

/** Trajectoire salariale servie, conservée d'un rechargement à l'autre. */
let trajectoireCourante: string | undefined;

/**
 * Dernier état monté. Un changement de thème système doit pouvoir redessiner
 * l'interface sans refaire un appel au serveur.
 */
let dernierEtat: {
  d: Charge;
  identite: Identite;
  nav: ModulesServis;
  navigationSecours: boolean;
  parametres?: Partial<ParametresSimulateur> | undefined;
} | null = null;

async function demarrer(pageInitiale?: string, trajectoire?: string): Promise<void> {
  if (trajectoire) trajectoireCourante = trajectoire;
  try {
    const identite = await session();
    if (identite.refus) {
      accesReserve(identite.refus.message, identite.refus.accueil);
      return;
    }
    if (!identite.authentifie && new URLSearchParams(window.location.search).get('session') === 'bloquee') {
      // Le serveur a interrompu un aller-retour avec compagnies.org : un nouveau
      // renvoi bouclerait. On propose de repartir d'une session neuve.
      accesReserve(
        "Votre session SCC n'a pas pu être reprise par le budget. Déconnectez-vous puis reconnectez-vous ; " +
          "si le problème persiste, prévenez l'équipe du SCC.",
        null,
      );
      return;
    }
    if (!identite.authentifie) {
      window.location.href = `${identite.connexion ?? '/connexion'}?suite=${encodeURIComponent(
        window.location.pathname + window.location.search + window.location.hash,
      )}`;
      return;
    }

    const idSimulation = new URLSearchParams(window.location.search).get('simulation');
    let parametres: Partial<ParametresSimulateur> | undefined;
    if (idSimulation) {
      parametres = (await chargerSimulation(idSimulation).catch(() => null))?.parametres;
    }

    const [d, modulesCharges] = await Promise.all([
      tableauDeBord(parametres, trajectoireCourante),
      chargerModules().catch((e: unknown) => {
        // Session expirée ou refusée : même traitement que le tableau de bord.
        if (e instanceof ErreurApi && (e.statut === 401 || e.statut === 403)) throw e;
        return null;
      }),
    ]);
    const navigationSecours = modulesCharges === null;
    const nav = modulesCharges ?? NAVIGATION_DE_SECOURS;
    if (pageInitiale) window.location.hash = pageInitiale;
    dernierEtat = { d, identite, nav, navigationSecours, parametres };
    monter(d, identite, nav, parametres, navigationSecours);
  } catch (e) {
    if (e instanceof ErreurApi && e.statut === 401) {
      erreur('Votre session a expiré.', e.connexion ?? '/connexion');
      return;
    }
    erreur(
      "Le tableau de bord n'a pas pu être chargé. L'incident est journalisé côté serveur ; " +
        'réessayez dans quelques instants.',
    );
  }
}

// Le thème est posé avant le premier rendu des vues : les graphiques lisent
// leurs couleurs dans la feuille de style au moment où ils se dessinent.
initialiserTheme(() => {
  if (dernierEtat) {
    monter(
      dernierEtat.d,
      dernierEtat.identite,
      dernierEtat.nav,
      dernierEtat.parametres,
      dernierEtat.navigationSecours,
    );
  }
});

void demarrer();
